import { computed, reactive, toRaw, watch } from 'vue';
import { type LocationQuery, useRoute, useRouter } from 'vue-router';
import {
  type FilterSchema,
  filterSchema,
  type RangeSchema,
  rangeSchema,
} from './filters.config';

// Define the type for the reactive state from the schemas
type FilterState = {
  -readonly [K in keyof typeof filterSchema]: (typeof filterSchema)[K]['defaultValue'];
} & {
  -readonly [K in keyof typeof rangeSchema]: [
    (typeof filterSchema)[(typeof rangeSchema)[K][0]]['defaultValue'],
    (typeof filterSchema)[(typeof rangeSchema)[K][1]]['defaultValue'],
  ];
};

// Helper to get the aggregated default state
function getDefaultState(): FilterState {
  const state = {} as FilterState;
  // Get all primitive defaults first
  const primitiveDefaults = Object.fromEntries(
    Object.entries(filterSchema).map(([key, config]) => [
      key,
      config.defaultValue,
    ]),
  ) as { [K in keyof FilterSchema]: FilterSchema[K]['defaultValue'] };

  // Create aggregated range properties
  for (const rangeKey in rangeSchema) {
    const key = rangeKey as keyof RangeSchema;
    const primitiveKeys = rangeSchema[key];
    state[key] = [
      primitiveDefaults[primitiveKeys[0]],
      primitiveDefaults[primitiveKeys[1]],
    ];
  }

  // Create a Set of keys that are part of ranges for efficient lookup
  const primitiveKeysInRanges = new Set<string>(
    Object.values(rangeSchema).flat(),
  );

  // Add all other non-range properties
  for (const key in filterSchema) {
    if (!primitiveKeysInRanges.has(key)) {
      const typedKey = key as keyof Omit<FilterState, keyof RangeSchema>;
      (state as any)[typedKey] = primitiveDefaults[typedKey];
    }
  }
  return state;
}

export function urlFilters() {
  const route = useRoute();
  const router = useRouter();
  const filters = reactive(getDefaultState());

  // --- PARSER + AGGREGATOR: (URL Query -> Reactive State) ---
  function parseQuery(query: LocationQuery): void {
    const newState = getDefaultState();
    const primitives: { [key: string]: any } = {};

    // Parse all primitives from URL query based on filterSchema
    for (const key in filterSchema) {
      const value = query[key];
      if (value !== undefined) {
        const config = filterSchema[key as keyof FilterSchema];

        if (config.type === Boolean) primitives[key] = value === 'true';
        else if (config.type === Number) primitives[key] = Number(value);
        else if (config.type === Array)
          primitives[key] = Array.isArray(value) ? value : [value];
        else primitives[key] = value; // Default to string
      }
    }

    // Aggregate into final state object
    for (const key in newState) {
      if (rangeSchema[key as keyof RangeSchema]) {
        // If it's a range key (e.g., 'date')
        const [key1, key2] = rangeSchema[key as keyof RangeSchema];
        (newState as any)[key] = [
          primitives[key1] ?? (newState as any)[key][0],
          primitives[key2] ?? (newState as any)[key][1],
        ];
      } else {
        // It's a simple key
        (newState as any)[key] = primitives[key] ?? (newState as any)[key];
      }
    }
    Object.assign(filters, newState);
  }

  // --- SERIALIZER + DISAGGREGATOR: (Reactive State -> URL Query) ---
  function updateFilters(newPartialFilters: Partial<FilterState>): void {
    let aDataFilterHasChanged = false;
    const currentFilters = toRaw(filters);

    for (const key in newPartialFilters) {
      if (key === 'page') {
        continue;
      }

      const newValue = newPartialFilters[key as keyof Partial<FilterState>];
      const currentValue = currentFilters[key as keyof FilterState];

      if (JSON.stringify(newValue) !== JSON.stringify(currentValue)) {
        aDataFilterHasChanged = true;
        break; // A change was found
      }
    }

    const finalNewFilters = { ...newPartialFilters };

    if (aDataFilterHasChanged) {
      finalNewFilters.page = 1;
    }

    // Apply updates to reactive state first
    Object.assign(filters, finalNewFilters);

    const query: LocationQuery = {};
    const rawFilters = toRaw(filters);

    // Disaggregate ranges and handle simple properties
    for (const key in rawFilters) {
      const filterKey = key as keyof FilterState;
      const value = rawFilters[filterKey];

      if (rangeSchema[filterKey as keyof RangeSchema]) {
        // It's a range, so disaggregate
        const [key1, key2] = rangeSchema[filterKey as keyof RangeSchema];
        const [val1, val2] = value as [any, any];
        const defaultVal1 = filterSchema[key1].defaultValue;
        const defaultVal2 = filterSchema[key2].defaultValue;

        if (val1 !== defaultVal1 && val1 != null) query[key1] = String(val1);
        if (val2 !== defaultVal2 && val2 != null) query[key2] = String(val2);
      } else {
        // It's a simple property
        const defaultConfig = filterSchema[filterKey as keyof FilterSchema];
        if (
          defaultConfig &&
          JSON.stringify(value) !== JSON.stringify(defaultConfig.defaultValue)
        ) {
          query[key] = value as any;
        }
      }
    }
    router.push({ query, hash: route.hash });
  }

  /**
   * Resets all filters to their default values.
   */
  function resetFilters(): void {
    const defaultState: Record<string, any> = getDefaultState();

    // Preserve people
    delete defaultState.adultCount;
    delete defaultState.juniorCount;
    delete defaultState.childCount;
    delete defaultState.infantCount;

    // Preserve date range
    delete defaultState.date;
    delete defaultState.startDate;
    delete defaultState.endDate;

    console.log(defaultState);

    updateFilters(defaultState);
  }

  /**
   * A computed property that returns true if all filters are currently set to their default values.
   * @returns {Boolean}
   */
  const areDefaultFilters = computed<boolean>(() => {
    const defaultState: Record<string, any> = getDefaultState();

    // Clone the current state
    const currentState: Record<string, any> = JSON.parse(
      JSON.stringify(filters),
    );

    // Ignore people counts
    delete currentState.adultCount;
    delete currentState.juniorCount;
    delete currentState.childCount;
    delete currentState.infantCount;
    delete defaultState.adultCount;
    delete defaultState.juniorCount;
    delete defaultState.childCount;
    delete defaultState.infantCount;

    // Ignore date range
    delete currentState.date;
    delete currentState.startDate;
    delete currentState.endDate;
    delete defaultState.date;
    delete defaultState.startDate;
    delete defaultState.endDate;

    // deep-compare the two objects
    return JSON.stringify(defaultState) === JSON.stringify(currentState);
  });

  // Sync on load and on back/forward navigation
  watch(
    () => route.query,
    (newQuery) => {
      parseQuery(newQuery);
    },
    { immediate: true, deep: true },
  );

  return {
    filters,
    updateFilters,
    resetFilters,
    areDefaultFilters,
  };
}
